import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAzureData } from './AzureContext';
import {
  chat,
  fetchAllModels,
  fetchIndexes,
  fetchModels,
  fetchPlaygroundHistory,
  previousMessages,
  playgroundSavePrompt,
  comprarePromptHistoryAPI,
  compareRunAPI,
  runHistoryAPI,
  deletePlaygroundHistoryAPI,
  deleteCompareHistory,
  editChat
} from '../service/apiServices';
import { captureRejectionSymbol } from 'events';
type PromptPlaygroundContextType = {
  models: Model[] | null;
  error: Error | any | null;
  isLoading: boolean;
  //
  playgroundData: any;
  playgroundDetails: () => void;
  indexData: [];
  indexDetails: (a: any) => void;
  chatData: {};
  ChatFunction: () => void;
  clearDeductionData: () => void;
  deleteCompareChat: (id: string) => void;
  // /
  MessageHistoryFunction: (a: any) => void;
  messageHistory: any;
  PlaygroundChatDeleteFunction: any;
  modelDataFunction: any;
  modelData: any;
  EditChatMessageFunction: any;
  editChatMessage: any;
  comparePromptHistory: any;
  //
  compareRunFunction: any;
  compareRun: any;

  //
  runHistory: any;
  runHistoryFunction: any;
};
export type Model = {
  model_id: string;
  model_name: string;
  client_id: string;
};

type PromptPlaygroundContextProviderProps = {
  children: ReactNode;
};
export const PromptPlaygroundContext = createContext<PromptPlaygroundContextType | null>(null);
export const PromptPlaygroundContextProvider = ({
  children
}: PromptPlaygroundContextProviderProps) => {
  const [models, setModels] = useState<Model[] | null>(null);
  const { apiToken } = useAzureData();
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const getAllModels = async () => {
    try {
      const result = await fetchAllModels(apiToken);
      setModels(result);
    } catch (error: any) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => getAllModels(), 1000);
    return () => clearTimeout(timer);
  }, []);

  const promptPlaygrounddata = useMemo(() => ({ models, error, isLoading }), [apiToken, isLoading]);

  const [playgroundData, setPlaygroundData] = useState({ data: {}, error: '', loading: false });
  const playgroundDetails = async () => {
    setPlaygroundData((prev) => ({ ...prev, loading: true }));

    try {
      const playGroundHistory = await fetchPlaygroundHistory(apiToken);
      setPlaygroundData((prev) => ({ ...prev, data: playGroundHistory }));
    } catch (err: any) {
      setPlaygroundData((prev) => ({ ...prev, error: err }));
    } finally {
      setPlaygroundData((prev) => ({ ...prev, loading: false }));
    }
  };

  const playground = useMemo(
    () => ({
      playgroundDetails,
      playgroundData
    }),
    // [playgroundData, playgroundDetails]
    [playgroundData]
  );

  const [indexData, setIndexData] = useState({
    data: {},
    modalData: {},
    error: '',
    loading: false
  });
  const indexDetails = async (selectedApplication: string) => {
    if (!selectedApplication) {
      return;
    }

    setIndexData((prev) => ({ ...prev, loading: true }));
    try {
      const indexes = await fetchIndexes(apiToken, selectedApplication);
      setIndexData((prev) => ({ ...prev, data: indexes }));
    } catch (err: any) {
      setIndexData((prev) => ({ ...prev, error: err }));
    } finally {
      setIndexData((prev) => ({ ...prev, loading: false }));
    }
  };

  //
  const [modelData, setModelDataData] = useState({
    modalData: {},
    error: '',
    loading: false
  });
  const modelDataFunction = async (token: string) => {
    setModelDataData((prev) => ({ ...prev, loading: true }));
    try {
      const models = await fetchModels(apiToken);

      setModelDataData((prev) => ({ ...prev, data: models }));
    } catch (err: any) {
      setModelDataData((prev) => ({ ...prev, error: err }));
    } finally {
      setModelDataData((prev) => ({ ...prev, loading: false }));
    }
  };
  const indexes = useMemo(
    () => ({
      indexData,
      indexDetails,
      //
      modelData,
      modelDataFunction
    }),
    [indexDetails, indexData]
  );

  //

  const [chatData, setChatData] = useState({ data: {}, loading: false, error: '' });
  const ChatFunction = async (payload: any) => {
    setChatData((prev) => ({ ...prev, loading: true }));
    try {
      const data = await chat(apiToken, payload);
      setChatData((prev) => ({ ...prev, data: data, loading: false }));
    } catch (err) {
      console.log(err);
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setComparePromptHistory((prev) => ({ ...prev, errorMessage: err, loading: false }));

      alert(err);
    } finally {
      setChatData((prev) => ({ ...prev, loading: false }));
    }
  };

  const ChatFunctionality = useMemo(
    () => ({
      chatData,
      ChatFunction
    }),
    [chatData]
  );

  // previousMessages
  const [messageHistory, setMessageHistory] = useState({ data: {}, loading: false, error: '' });
  const MessageHistoryFunction = async (payload: any) => {
    setMessageHistory((prev) => ({ ...prev, loading: true }));
    try {
      const data = await previousMessages(apiToken, payload);
      setMessageHistory((prev) => ({ ...prev, data: data, loading: false }));
    } catch (err) {
      setMessageHistory((prev) => ({ ...prev, error: err, loading: false }));
      console.log(err);
      alert(err);
    } finally {
      setMessageHistory((prev) => ({ ...prev, loading: false }));
    }
  };

  const MessageHistoryFunctionality = useMemo(
    () => ({
      MessageHistoryFunction,
      messageHistory
    }),
    [MessageHistoryFunction, messageHistory]
  );

  // saving prompt for Prompt Library
  const [savePromptStatus, setSavePromptStatus] = useState({ loading: false, data: {}, error: '' });
  const savePromptPlaygroundFunction = async (payload: any) => {
    setSavePromptStatus((prev) => ({ ...prev, loading: true }));
    try {
      const data = await playgroundSavePrompt(apiToken, payload);
      setSavePromptStatus((prev) => ({ ...prev, data: data, loading: false }));
    } catch (err) {
      setSavePromptStatus((prev) => ({ ...prev, error: err, loading: false }));
      console.log(err);
      alert(err);
    } finally {
      setSavePromptStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const savePromptFunction = useMemo(
    () => ({
      savePromptPlaygroundFunction,
      savePromptStatus
    }),
    [savePromptStatus, savePromptPlaygroundFunction]
  );

  // compare prompt History
  const [comparePromptHistory, setComparePromptHistory] = useState({
    data: {},
    error: '',
    loading: false
  });

  const comparePromptHistoryFunction = async () => {
    setComparePromptHistory((prev) => ({ ...prev, loading: true }));
    try {
      const data = await comprarePromptHistoryAPI(apiToken);
      setComparePromptHistory((prev) => ({ ...prev, data: data, loading: false }));
    } catch (err) {
      console.log(err);
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setComparePromptHistory((prev) => ({ ...prev, errorMessage: errorMessage, loading: false }));
    } finally {
      setComparePromptHistory((prev) => ({ ...prev, loading: false }));
    }
  };

  const comparePromptMemo = useMemo(
    () => ({ comparePromptHistoryFunction, comparePromptHistory }),
    [comparePromptHistoryFunction, comparePromptHistory]
  );

  //compare Run
  const [compareRun, setCompareRun] = useState({
    data: {},
    error: '',
    loading: false
  });

  const compareRunFunction = async (payload: any) => {
    setCompareRun((prev) => ({ ...prev, loading: true }));
    try {
      const data = await compareRunAPI(apiToken, payload);
      // console.log(data, 'cs');
      setCompareRun((prev) => ({ ...prev, data: data }));
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Something went wrong';
      setCompareRun((prev) => ({ ...prev, error: error }));
    } finally {
      setCompareRun((prev) => ({ ...prev, loading: false }));
    }
  };

  const compareRunMemo = useMemo(
    () => ({ compareRunFunction, compareRun }),
    [compareRunFunction, compareRun]
  );

  // for fetching history of specific run compare
  const [runHistory, setRunHistory] = useState({
    data: {},
    error: '',
    loading: false
  });

  const runHistoryFunction = async (id: string) => {
    setRunHistory((prev) => ({ ...prev, loading: true }));
    try {
      const data = await runHistoryAPI(apiToken, id);
      setRunHistory((prev) => ({ ...prev, data: data }));
    } catch (err) {
      console.log(err);
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setRunHistory((prev) => ({ ...prev, errorMessage: errorMessage }));
    } finally {
      setRunHistory((prev) => ({ ...prev, loading: false }));
    }
  };

  const runHistoryMemo = useMemo(
    () => ({
      runHistoryFunction,
      runHistory
    }),
    [runHistory, runHistoryFunction]
  );

  // Deleting PlaygroundChatHistory
  const [deleteStatus, setDeleteStatus] = useState({ data: {}, loading: false, error: '' });

  const PlaygroundChatDeleteFunction = async (playground_id: string) => {
    setDeleteStatus((prev) => ({ ...prev, loading: true }));
    try {
      const data = await deletePlaygroundHistoryAPI(apiToken, playground_id);
      setDeleteStatus((prev) => ({ ...prev, data: data }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setDeleteStatus((prev) => ({ ...prev, error: errorMessage }));
      alert(err);
    } finally {
      setDeleteStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const PlaygroundChatDeleteMemo = useMemo(
    () => ({
      PlaygroundChatDeleteFunction,
      deleteStatus
    }),
    [deleteStatus, PlaygroundChatDeleteFunction]
  );
  //
  const clearDeductionData = () => {
    setChatData({ data: {}, loading: false, error: '' });
    setCompareRun({
      data: {},
      error: '',
      loading: false
    });
  };

  const [compareDelete, setCompareDelete] = useState({ data: {}, error: '', loading: false });

  const deleteCompareChat = async (id: any) => {
    setCompareDelete((prev) => ({ ...prev, loading: true }));
    try {
      const playGroundHistory = deleteCompareHistory(apiToken, id);
      setCompareDelete((prev) => ({ ...prev, data: playGroundHistory }));
    } catch (err: any) {
      setCompareDelete((prev) => ({ ...prev, error: err }));
    } finally {
      setCompareDelete((prev) => ({ ...prev, loading: false }));
    }
  };

  const [editChatMessage, setEditChatMessage] = useState({ data: {}, loading: false, error: '' });
  const EditChatMessageFunction = async (payload: any) => {
    setEditChatMessage((prev) => ({ ...prev, loading: true }));
    try {
      const playGroundHistory = await editChat(payload, apiToken);
      setEditChatMessage((prev) => ({ ...prev, data: playGroundHistory }));
    } catch (err: any) {
      setEditChatMessage((prev) => ({ ...prev, error: err }));
    } finally {
      setEditChatMessage((prev) => ({ ...prev, loading: false }));
    }
  };

  const editChatMessageMemo = useMemo(
    () => ({ editChatMessage, EditChatMessageFunction }),
    [editChatMessage]
  );

  return (
    <PromptPlaygroundContext.Provider
      value={{
        ...promptPlaygrounddata,
        ...playground,
        ...indexes,
        ...ChatFunctionality,
        ...MessageHistoryFunctionality,
        ...savePromptFunction,
        ...comparePromptMemo,
        //
        ...compareRunMemo,
        // /
        ...runHistoryMemo,
        //
        ...PlaygroundChatDeleteMemo,
        // /
        clearDeductionData,
        deleteCompareChat,

        ...editChatMessageMemo
      }}>
      {children}
    </PromptPlaygroundContext.Provider>
  );
};
export const usePromptPlaygroundContext = () => {
  const context = useContext(PromptPlaygroundContext);
  if (context === null) {
    throw new Error('usePromptPlaygroundContext must be used within a DataProvider');
  }
  return context;
};
